import { Api } from '@automapa/svelte-automapa-template';
import { z } from 'zod';
import {
    AccountCreateResponseSchema,
    type AccountCreateResponseType,
    AccountRequestSchema,
    type AccountRequestType,
    AccountResponseSchema,
    type AccountResponseType,
} from '../Settings/SettingsApi';
import {
    CustomerSettingsReadonlySchema,
    type CustomerSettingsReadonlyType,
} from '../Settings/settings';
import type { ApiInterface } from '../../utilities/ApiInterface';

export class AdminApi implements ApiInterface {
    private static _endpoint: string = 'admin';

    public static async createAccount(
        api: Api,
        name: string,
        email: string,
        expiration_date: string,
        settings_readonly: CustomerSettingsReadonlyType
    ): Promise<AccountCreateResponseType> {
        return api.getData<AccountRequestType, AccountCreateResponseType>(
            'post',
            `/${this._endpoint}/create_account`,
            {
                name,
                email,
                expiration_date,
                is_expired: false,
                settings_readonly,
                master_customer_id: null,
            },
            AccountRequestSchema,
            AccountCreateResponseSchema
        );
    }

    public static async getAccounts(api: Api): Promise<AccountResponseType[]> {
        return api.getData<any, AccountResponseType[]>(
            'post',
            `/${this._endpoint}/get_accounts`,
            {},
            z.any(),
            z.array(AccountResponseSchema)
        );
    }

    public static async updateExpirationDate(
        api: Api,
        customer_id: number,
        expiration_date: string | null
    ): Promise<any> {
        return api.getData<{ customer_id: number; expiration_date: string | null }, any>(
            'post',
            `/${this._endpoint}/update_expiration_date`,
            { customer_id, expiration_date },
            z.object({
                customer_id: z.number(),
                expiration_date: z
                    .string()
                    .regex(/^\d{4}-\d{2}-\d{2}$/i)
                    .nullable(),
            }),
            z.any()
        );
    }

    public static async updateSettingsReadonly(
        api: Api,
        customer_id: number,
        settings_readonly: CustomerSettingsReadonlyType
    ): Promise<CustomerSettingsReadonlyType> {
        return api.getData<
            { customer_id: number; settings_readonly: CustomerSettingsReadonlyType },
            any
        >(
            'post',
            `/${this._endpoint}/update_settings_readonly`,
            { customer_id, settings_readonly },
            z.object({
                customer_id: z.number(),
                settings_readonly: CustomerSettingsReadonlySchema,
            }),
            CustomerSettingsReadonlySchema
        );
    }

    public static async customerIdList(api: Api): Promise<{ id: number; name: string }[]> {
        return api.getData<any, { id: number; name: string }[]>(
            'post',
            `/${this._endpoint}/customer_id_list`,
            {},
            z.any(),
            z.array(z.object({ id: z.number(), name: z.string() }))
        );
    }
}
